import { Logger } from "@/apis/Logger";
import { isElectronApp } from "@/helpers/electron-helper";
import { VpnConnectionState } from "@/types/vpn";

/**
 * Whether the on-demand VPN status check is available in this environment.
 * True only inside the Electron app on a version that exposes the
 * `checkVpnStatus` preload bridge (NHE-6140). In the browser (or older app
 * versions) the check cannot run and callers should skip it entirely.
 */
export const isVpnStatusCheckAvailable = (): boolean =>
  isElectronApp() && typeof window.electronAPI?.checkVpnStatus === "function";

/**
 * Runs the on-demand VPN connection status check via the Electron preload
 * bridge.
 *
 * Fails open: any error (IPC failure, unexpected shape) resolves to
 * `"unknown"` so the caller never blocks the user on a broken check.
 *
 * @returns the parsed VPN connection state
 */
export const checkVpnConnectionStatus =
  async (): Promise<VpnConnectionState> => {
    try {
      const status = await window.electronAPI.checkVpnStatus?.();
      return status?.state ?? "unknown";
    } catch (error) {
      void Logger.errorErrorLike(
        "VPN status check failed; treating as unknown",
        error as Error
      );
      return "unknown";
    }
  };
